export interface ClientConfig {
	baseUrl: string;
}

// Emitted OpenAPI paths already carry the /api prefix (single origin) — the base
// stays empty unless the API lives on another origin.
const clientConfig: ClientConfig = {
	baseUrl: '',
};

export function initializeClient(config: { baseUrl?: string }): void {
	if (typeof config.baseUrl === 'string' && config.baseUrl.length > 0) {
		clientConfig.baseUrl = config.baseUrl;
	}
}

export class ApiError<T = unknown> extends Error {
	status: number;
	statusText: string;
	data?: T;

	constructor(status: number, statusText: string, data?: T) {
		const suffix =
			typeof data === 'string' && data.length > 0 ? ` - ${data}` : data ? ` - ${JSON.stringify(data)}` : '';
		super(`API request failed: ${status} ${statusText}${suffix}`);
		this.name = 'ApiError';
		this.status = status;
		this.statusText = statusText;
		this.data = data;
	}
}

function createUrl(path: string): URL {
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	const { baseUrl } = clientConfig;
	const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

	const fullUrl = `${normalizedBase}${normalizedPath}`;

	return fullUrl.startsWith('http')
		? new URL(fullUrl)
		: new URL(fullUrl, globalThis.location?.origin ?? 'http://localhost');
}

async function parseResponse<T>(response: Response): Promise<T> {
	if (response.status === 204) {
		return undefined as T;
	}

	return (await response.json()) as T;
}

async function parseErrorResponse(response: Response): Promise<unknown> {
	if (response.status === 204) {
		return undefined;
	}

	const contentType = response.headers.get('content-type') ?? '';
	if (contentType.includes('application/json')) {
		try {
			return await response.json();
		} catch {
			return undefined;
		}
	}

	const text = await response.text();
	return text.length > 0 ? text : undefined;
}

export async function customFetch<T>(url: string, options?: RequestInit): Promise<T> {
	const response = await fetch(createUrl(url), {
		...options,
		credentials: 'include',
	});

	if (!response.ok) {
		const errorBody = await parseErrorResponse(response);
		throw new ApiError(response.status, response.statusText, errorBody);
	}

	return parseResponse<T>(response);
}
